import axios from "axios";
import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";


const adminAuthContext = createContext();


export function AdminAuthContextProvider({ children }) {
  const [loading, setLoading] = useState(false);
  const [admin, setAdmin] = useState("");
  const [adminName, setAdminName] = useState("")
  const [isCollapsed, setIsCollapsed] = useState(false);
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  // Admin-managed garment types (Shirt / Hoodie / T-shirt / Shacket / Pant).
  // Separate from `categories`, which are the collection names.
  const [garmentTypes, setGarmentTypes] = useState([]);
  const [products, setProducts] = useState([]);
  const [name, setName] = useState("");
  const [page, setPage] = useState(1)
  const [nextPage, setNextPage] = useState(false)


  const toggleSidebar = () => {
    setIsCollapsed((prevState) => !prevState);
  };


  // Setup Axios instance
  const axiosInstance = axios.create({
    baseURL: "https://admin.riymay.com/api/v1/admin",
  });


  // Function to log in the admin
  const login = async (username, password) => {
    setLoading(true);
    try {
      setAdminName(username)
      const response = await axiosInstance.post("/login", { username, password });

      // Check if the response contains the expected data (accessToken in this case)
      if (response.data && response.data.accessToken) {
        localStorage.setItem("admintoken", response.data.accessToken);
        setAdmin(username);
        toast.success("Logged in successfully!", {
          autoClose: 20000, // Duration in milliseconds (20 seconds)
        });
        navigate("/");
      } else {
        throw new Error("Login failed: Invalid server response");
      }
      } catch (error) {
        toast.error(error.response?.data?.message, {
          autoClose: 20000, // Duration in milliseconds (20 seconds)
        } || "Login failed");
      } finally {
        setLoading(false);
      }
  };


  // Refresh token function
  const refreshAccessToken = async () => {
    try {
      const response = await axiosInstance.get("/refresh-token", {
        withCredentials: true, // Required to send cookies in request
      });
      localStorage.setItem("admintoken", response.data.accessToken);
      return response.data.accessToken;
    } catch (error) {
      logout();
      toast.error("Session expired, please log in again.");
    }
  };


  // Logout function
  const logout = () => {
    localStorage.removeItem("admintoken");
    // setAdmin("");
    navigate("/login"); 
  };


  // Add Axios interceptor for requests to attach token and handle refresh
  axiosInstance.interceptors.request.use(
    async (config) => {
      const token = localStorage.getItem("admintoken");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );


  // Add Axios interceptor for responses to refresh token on 401
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      // Check if the error status is 401 and a token exists in localStorage
      const token = localStorage.getItem("admintoken");
      if (error.response && error.response.status === 401 && token) {
        try {
          const newAccessToken = await refreshAccessToken();
          if (newAccessToken) {
            // Update the original request's Authorization header and retry
            error.config.headers.Authorization = `Bearer ${newAccessToken}`;
            return axiosInstance(error.config);
          }
        } catch (refreshError) {
          // Logout if refresh fails
          logout();
        }
      }
      return Promise.reject(error);
    }
  );

  const addClothingProduct = async (productInfo) => {
  
    try {
      const token = localStorage.getItem('admintoken'); // Retrieve the token from localStorage

      const response = await fetch('https://admin.riymay.com/api/v1/admin/add-clothing-product', {
        method: 'POST',
        headers: {
            // 'Content-Type': 'application/json', // Specify the content type
            'Authorization': `Bearer ${token}`, // Add the Bearer token
        },
        body: productInfo,
      });
  
      if (response.ok) { 
        const responseData = await response.json();
        toast.success(responseData.message || 'Product added successfully');
        navigate(0);
      } else {
        toast.error('Unexpected response from server. Please try again.');
      }
    } catch (error) {
     
      toast.error(error.message || 'Failed to add product');
    }
  };
  
  

  //fetch products
  const fetchProducts = async (page) => {
    const token = localStorage.getItem('admintoken'); // Retrieve the token from localStorage

    axios
        .get(`https://admin.riymay.com/api/v1/admin/search-clothing-product?searchQuery=${name}&page=${page}&limit=9`,{
          headers: {
            'Authorization': `Bearer ${token}`, // Add the Bearer token
        },
        })
        .then((response) => {
          setProducts(response.data.products);
          setNextPage(response.data.nextPage) // assuming the data structure matches
        })
        .catch((error) => {
          console.error("Error fetching data: ", error);
        });
  }


  //fetch main categories
  const fetchCategories = async () => {
    try {
    const token = localStorage.getItem('admintoken'); // Retrieve the token from localStorage
      const response = await axios.get(
        "https://admin.riymay.com/api/v1/admin/get/all/main/product-categories",
        {
          headers: {
            'Authorization': `Bearer ${token}`, // Add the Bearer token
        },
        }
      );
      setCategories(response.data.response);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  
  // fetch garment types. The whole list, active and inactive, because the
  // management page has to show the switched-off ones so they can be switched
  // back on. The product form filters to the active ones itself.
  const fetchGarmentTypes = async () => {
    try {
      const response = await axios.get(
        "https://admin.riymay.com/api/v1/admin/get/all/garment-types"
      );
      setGarmentTypes(Array.isArray(response.data.response) ? response.data.response : []);
    } catch (error) {
      console.error("Error fetching garment types:", error);
    }
  };


  //create main categories
  const addMainCategory = async (categoryInfo) => {
    try {
    const token = localStorage.getItem('admintoken'); // Retrieve the token from localStorage

      const response = await axiosInstance.post('/create/main/product-category', categoryInfo,
        {
          headers: {
            'Authorization': `Bearer ${token}`, // Add the Bearer token
          },
        }
      );
  
      if (response.status === 201) { // Check for a successful response
        toast.success(response.data); // Notify success
      } else {
        toast.error("Unexpected response from server. Please try again.");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add product");
    }
  };
  


  useEffect(() => {
    const token = localStorage.getItem("admintoken");
    if (token) {
      setAdmin(admin);
      toggleSidebar();
      fetchCategories()
      fetchGarmentTypes()
      fetchProducts()
    }
  }, [admin]);

  useEffect(() => {
    const token = localStorage.getItem("admintoken");
    if (token) {
      fetchProducts()
    }
  }, [name])


  return (
    <adminAuthContext.Provider
      value={{
        admin,
        setAdmin,
        adminName,
        login,
        logout,
        loading,
        setLoading,
        isCollapsed,
        toggleSidebar,
        logout,
        addClothingProduct,

        fetchCategories,
        setCategories,
        categories,

        fetchGarmentTypes,
        setGarmentTypes,
        garmentTypes,

        products,
        name, setName,
        page, setPage,
        setProducts,
        fetchProducts,
        nextPage

      }}
    >
      {children}
    </adminAuthContext.Provider>
  );
}


export function useAdminAuth() {
  return useContext(adminAuthContext);
}












